import React from 'react';
import dayjs from 'dayjs';
import { EngineCommercialA4Print } from '@/components/print/EngineCommercialPrints';
import type { PmvHireAgreement, PmvHireRate } from '@/services/pmv-hire.service';
import { formatHireMoney } from '@/services/pmv-hire.service';
import { openPmvPrintOverlay } from '@/utils/openPmvPrintOverlay';

export type HireAgreementPrintContext = {
  agreement: PmvHireAgreement;
  rates: PmvHireRate[];
  supplierLabel?: string;
  equipmentLabel?: string;
  projectLabel?: string;
  poLabel?: string;
  poLineLabel?: string;
  workFrontLabel?: string;
  effectiveRateLabel?: string;
};

function fmtDate(v?: string | null): string {
  if (!v) return '';
  const d = dayjs(v);
  return d.isValid() ? d.format('DD MMM YYYY') : String(v);
}

function fmtDateTime(v?: string | null): string {
  if (!v) return '';
  const d = dayjs(v);
  return d.isValid() ? d.format('DD MMM YYYY HH:mm') : String(v);
}

function ratePrimary(r: PmvHireRate): string {
  const basis = r.rate_basis || '';
  if (basis === 'HOURLY' || basis === 'SHIFT') return formatHireMoney(r.hourly_rate, r.currency);
  if (basis === 'DAILY' || basis === 'WEEKLY') return formatHireMoney(r.daily_rate, r.currency);
  if (basis === 'MONTHLY') return formatHireMoney(r.monthly_rate, r.currency);
  const any =
    r.hourly_rate ?? r.daily_rate ?? r.monthly_rate;
  return formatHireMoney(any, r.currency);
}

/** Formal hire agreement print via shared pagination engine. */
export const PrintableHireAgreement: React.FC<
  HireAgreementPrintContext & { onClose?: () => void }
> = ({
  agreement: a,
  rates,
  supplierLabel,
  equipmentLabel,
  projectLabel,
  poLabel,
  poLineLabel,
  workFrontLabel,
  effectiveRateLabel,
  onClose,
}) => {
  const activeRates = rates.filter((r) => r.is_active !== false);
  const lines = (activeRates.length ? activeRates : rates).map((r, i) => ({
    lineNumber: i + 1,
    description: r.label || r.rate_code || 'Rate',
    cells: {
      code: r.rate_code || '—',
      basis: r.rate_basis || '—',
      rate: ratePrimary(r),
      min_h: r.daily_minimum_hours == null || r.daily_minimum_hours === '' ? '—' : String(r.daily_minimum_hours),
      standby: r.standby_pct == null || r.standby_pct === '' ? '—' : `${r.standby_pct}%`,
      breakdown: r.breakdown_pct == null || r.breakdown_pct === '' ? '—' : `${r.breakdown_pct}%`,
      from: fmtDate(r.effective_from) || '—',
      until: fmtDate(r.effective_until) || 'Open',
    },
  }));

  if (!lines.length) {
    lines.push({
      lineNumber: 1,
      description: 'No rate schedule rows — rate Unavailable until configured',
      cells: {
        code: '—',
        basis: '—',
        rate: 'Unavailable',
        min_h: '—',
        standby: '—',
        breakdown: '—',
        from: '—',
        until: '—',
      },
    });
  }

  const responsibility = [
    a.operator_responsibility ? `Operator: ${a.operator_responsibility}` : null,
    a.fuel_responsibility ? `Fuel: ${a.fuel_responsibility}` : null,
    a.maintenance_responsibility ? `Maintenance: ${a.maintenance_responsibility}` : null,
    a.tyre_responsibility ? `Tyres: ${a.tyre_responsibility}` : null,
    a.mob_responsibility ? `Mobilization: ${a.mob_responsibility}` : null,
    a.demob_responsibility ? `Demobilization: ${a.demob_responsibility}` : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <EngineCommercialA4Print
      docType="pmv_hire_agreement"
      title="EQUIPMENT HIRE AGREEMENT"
      documentNumber={a.agreement_number || 'HIRE-AGR'}
      partyName={supplierLabel || 'Supplier'}
      letterhead
      onClose={onClose}
      introText={
        'Commercial hire profile for rented/leased equipment under PMV operational control. ' +
        'References Procurement Vendor and Purchase Order. Does not replace PO, Supplier Invoice, AP or GL. ' +
        'Working hours ≠ billable hours — hire calculation interprets verified Daily Logs against these terms.'
      }
      metaLines={[
        { label: 'Agreement No', value: a.agreement_number || '' },
        { label: 'Status', value: String(a.commercial_status || '') },
        { label: 'Ownership', value: a.ownership_type || '' },
        { label: 'Currency', value: a.currency || '' },
        { label: 'Supplier', value: supplierLabel || a.supplier_id || '' },
        { label: 'Equipment', value: equipmentLabel || a.pmv_profile_id || '' },
        { label: 'Project', value: projectLabel || '' },
        { label: 'Site', value: a.site_reference || '' },
        { label: 'Work front', value: workFrontLabel || '' },
        { label: 'Contract ref', value: a.contract_reference || '' },
        { label: 'Purchase Order', value: poLabel || '' },
        { label: 'PO line', value: poLineLabel || '' },
        { label: 'Tax treatment', value: a.tax_treatment_ref || '' },
        { label: 'Planned on-hire', value: fmtDate(a.planned_on_hire_date) },
        { label: 'Planned off-hire', value: fmtDate(a.planned_off_hire_date) },
        { label: 'Commercial on-hire', value: fmtDateTime(a.commercial_on_hire_at) },
        {
          label: 'Off-hire effective',
          value: fmtDateTime(a.commercial_off_hire_effective_at),
        },
        { label: 'Current rate', value: effectiveRateLabel || '' },
        { label: 'Responsibility', value: responsibility },
        { label: 'Notes', value: a.notes || '' },
      ].filter((m) => m.value)}
      columns={[
        { key: 'sno', label: '#' },
        { key: 'code', label: 'Code' },
        { key: 'description', label: 'Rate schedule' },
        { key: 'basis', label: 'Basis' },
        { key: 'rate', label: 'Rate', align: 'right' },
        { key: 'min_h', label: 'Min h/d', align: 'right' },
        { key: 'standby', label: 'Standby', align: 'right' },
        { key: 'breakdown', label: 'BD %', align: 'right' },
        { key: 'from', label: 'From' },
        { key: 'until', label: 'Until' },
      ]}
      lines={lines}
      finals={[
        { id: 'disclaimer', heightPx: 72, keepTogetherWithNext: false },
        { id: 'signoff', heightPx: 96, keepTogetherWithNext: false },
      ]}
      finalBlocks={{
        disclaimer: (
          <div style={{ fontSize: 10, lineHeight: 1.4 }}>
            <strong>Commercial notice.</strong> Charging stops at contractual OFF_HIRE_EFFECTIVE.
            Physical presence, GPS or delayed vendor pickup does not extend hire. Certified hire
            statements retain rate/term snapshots — later PO amendments do not rewrite certified history.
            Expected hire is not a posted GL expense.
          </div>
        ),
        signoff: (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 1fr',
              gap: 24,
              marginTop: 8,
              fontSize: 11,
            }}
          >
            <div>
              <div style={{ borderTop: '1px solid #333', paddingTop: 6 }}>Prepared by</div>
              <div>Name / Date</div>
            </div>
            <div>
              <div style={{ borderTop: '1px solid #333', paddingTop: 6 }}>Commercial review</div>
              <div>Name / Date</div>
            </div>
            <div>
              <div style={{ borderTop: '1px solid #333', paddingTop: 6 }}>Vendor acknowledgement</div>
              <div>Name / Date (if used)</div>
            </div>
          </div>
        ),
      }}
    />
  );
};

/** Mount print overlay and open browser print dialog via GenericPaginatedDocument. */
export function openHireAgreementPrint(ctx: HireAgreementPrintContext): void {
  openPmvPrintOverlay((onClose) => <PrintableHireAgreement {...ctx} onClose={onClose} />);
}

export default PrintableHireAgreement;
